import { useCallback, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "@/integrations/supabase/config";

// Shared, reader-clean Server-Sent-Events chat hook for "Ask the research hub".
//
// Lifted out of the ops ResearchChatSession so the reader Ask and the ops chat share one
// parser and the citation fix lands once. Differences from the ops version:
//  • base URL comes from config (no hard-coded literal in a component),
//  • the live backend streams `text_delta` (with literal `[source: <uuid>]` markers) → `done`
//    and never emits `final_answer`/`session`, so we reconcile + persist entirely client-side,
//  • `[source: <uuid>]` markers become footnote chips ([1], [2]…) — never a raw uuid (and are
//    stripped while streaming so a uuid never flashes mid-answer),
//  • cost / turns / tool JSON are hidden (debug only),
//  • the client persists the chat tables itself (the reader backend writes nothing).

export interface SourceRef {
  n: number;
  sourceId: string;
  title: string;
  axis?: string;
  chunkId?: string;
}

export interface ReaderMessage {
  role: "user" | "assistant";
  /** Raw text — user input, or the assistant's settled answer (or streamed deltas while live). */
  content: string;
  /** Assistant only: reconciled text with `[source: uuid]` → `[n]` footnote links. */
  display?: string;
  /** Assistant only: the numbered sources behind the answer (used citations, in order). */
  sources?: SourceRef[];
  /** Assistant only: stable per-turn key so footnote anchor ids never collide across turns. */
  turnKey?: string;
  /** Quiet progress line while streaming ("thinking", "searching documents…"). */
  status?: string;
  done?: boolean;
  stopped?: boolean;
  error?: string;
}

type CiteMeta = { title: string; axis?: string; chunkId?: string };

// Matches a `[source: <uuid>]` marker. Shared by the reconcile and the streaming stripper so the
// two never drift apart.
const SOURCE_MARKER_RE = /\[source:\s*[0-9a-fA-F-]{8,}\]/g;

// Remove raw `[source: uuid]` markers from text shown mid-stream (we keep the raw copy internally
// for the final reconcile + persistence). Collapses the whitespace a removed marker leaves behind.
function stripMarkers(text: string): string {
  return text.replace(SOURCE_MARKER_RE, "").replace(/[ \t]{2,}/g, " ");
}

function toolPhrase(name: string): string {
  switch (name) {
    case "retrieve_hybrid":
      return "searching documents";
    case "fred_lookup":
      return "looking up market data";
    case "kg_lookup":
    case "kg_route":
      return "checking the knowledge graph";
    case "remember_about_me":
      return "recalling context";
    default:
      return "searching";
  }
}

function statusPhrase(phase: string): string {
  switch (phase) {
    case "loading_memory":
      return "recalling context";
    case "thinking":
      return "thinking";
    case "fallback_model":
    case "model_switch":
      return "thinking (backup model)";
    default:
      return "thinking";
  }
}

// Replace each `[source: <uuid>]` marker with a `[n]` footnote link and collect the ordered,
// de-duplicated sources. `turnKey` makes the anchor ids unique per message so footnote links from
// one turn don't jump to another turn's source list.
function reconcile(
  finalText: string,
  cites: Map<string, CiteMeta>,
  turnKey: string,
): { display: string; sources: SourceRef[] } {
  const order: string[] = [];
  const display = finalText.replace(SOURCE_MARKER_RE, (m) => {
    const idMatch = m.match(/([0-9a-fA-F-]{8,})/);
    const sid = (idMatch ? idMatch[1] : "").toLowerCase();
    if (!sid) return "";
    let idx = order.indexOf(sid);
    if (idx === -1) {
      order.push(sid);
      idx = order.length - 1;
    }
    const n = idx + 1;
    return `[\\[${n}\\]](#hub-src-${turnKey}-${n})`;
  });
  const sources: SourceRef[] = order.map((sid, i) => ({
    n: i + 1,
    sourceId: sid,
    title: cites.get(sid)?.title || "Source",
    axis: cites.get(sid)?.axis,
    chunkId: cites.get(sid)?.chunkId,
  }));
  return { display: display.trim(), sources };
}

// Same reconciliation from a persisted citations array (used when replaying chat history).
export function reconcileFromCitations(
  text: string,
  citations: Array<{ source_id?: string; title?: string; axis?: string; chunk_id?: string }> | null | undefined,
  turnKey: string,
): { display: string; sources: SourceRef[] } {
  const map = new Map<string, CiteMeta>();
  for (const c of citations ?? []) {
    if (c?.source_id)
      map.set(String(c.source_id).toLowerCase(), { title: c.title || "Source", axis: c.axis, chunkId: c.chunk_id });
  }
  return reconcile(text, map, turnKey);
}

// Build the persisted citations array (reconcileFromCitations' input shape) from the live sources.
function sourcesToCitations(sources: SourceRef[]): Array<{ source_id: string; title: string; axis?: string; chunk_id?: string }> {
  return sources.map((s) => ({ source_id: s.sourceId, title: s.title, axis: s.axis, chunk_id: s.chunkId }));
}

export function useResearchChatStream(initialSessionId?: string) {
  const qc = useQueryClient();
  const [messages, setMessages] = useState<ReaderMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState<string | undefined>(initialSessionId);
  // Mirror of the active session id read synchronously by `send`. A brand-new conversation's id is
  // created during persistence; this ref carries it forward so a follow-up turn reuses the same
  // session even before the `setSessionId` state update has flushed.
  const sessionIdRef = useRef<string | undefined>(initialSessionId);
  const abortRef = useRef<AbortController | null>(null);
  // Bumped whenever the active conversation changes (reset / loadThread). A stream captures the
  // value at send time; any patch whose token is stale is dropped, so an in-flight answer can never
  // bleed into a freshly-loaded or freshly-cleared conversation.
  const genRef = useRef(0);
  // Tracks the persisted message count for the active session so we can keep `message_count` in sync
  // without an extra round-trip. Set from the seed on reset/loadThread, +2 per persisted turn.
  const countRef = useRef(0);
  // True for the WHOLE turn — stream AND the persistence that follows it. `send` early-returns while
  // busy, and the UI disables every conversation-mutating control, so a quick follow-up can't race
  // the first turn's persist (which would otherwise create a second session / corrupt the count).
  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);

  const reset = useCallback((newSessionId?: string, seed: ReaderMessage[] = []) => {
    abortRef.current?.abort();
    genRef.current += 1;
    countRef.current = seed.length;
    sessionIdRef.current = newSessionId;
    setMessages(seed);
    setSessionId(newSessionId);
  }, []);

  const stop = useCallback(() => abortRef.current?.abort(), []);

  const send = useCallback(
    async (text: string) => {
      const query = text.trim();
      // One gate for the whole turn (stream + persist). Reading the ref (not the `streaming` state)
      // means a follow-up send is blocked until the previous turn's persistence has fully finished.
      if (!query || busyRef.current) return;
      busyRef.current = true;
      setBusy(true);

      const myGen = ++genRef.current;
      const turnKey =
        typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${myGen}`;

      // optimistic user turn + a live assistant turn
      setMessages((prev) => [
        ...prev,
        { role: "user", content: query, done: true },
        { role: "assistant", content: "", status: "thinking", turnKey },
      ]);
      setStreaming(true);

      const cites = new Map<string, CiteMeta>();
      let finalText: string | null = null;
      // Read the session id from the ref so a freshly-created session (set during the previous turn's
      // persist) is reused rather than re-created.
      let localSession = sessionIdRef.current;
      let streamedRaw = "";
      let errored = false;
      let result: { raw: string; sources: SourceRef[] } | null = null;

      // mutate the last (assistant) message — but only while this send still owns the conversation.
      const patchAssistant = (patch: Partial<ReaderMessage>) => {
        if (genRef.current !== myGen) return;
        setMessages((prev) => {
          const next = [...prev];
          const i = next.length - 1;
          if (i >= 0 && next[i].role === "assistant") next[i] = { ...next[i], ...patch };
          return next;
        });
      };

      // Reconcile + settle the assistant bubble exactly once. Returns the raw text + sources so the
      // caller can persist them; null if already settled (e.g. done after final_answer).
      let settled = false;
      const finalize = (extra: Partial<ReaderMessage> = {}): { raw: string; sources: SourceRef[] } | null => {
        if (settled) return null;
        settled = true;
        const raw = finalText ?? streamedRaw;
        const { display, sources } = reconcile(raw, cites, turnKey);
        patchAssistant({ content: raw, display, sources, done: true, status: undefined, ...extra });
        return { raw, sources };
      };

      const ac = new AbortController();
      abortRef.current = ac;

      try {
        const { data: sess } = await supabase.auth.getSession();
        const token = sess.session?.access_token;
        if (!token) throw new Error("not_signed_in");

        const history = messages.map((m) => ({ role: m.role, content: m.content }));
        const res = await fetch(`${SUPABASE_URL}/functions/v1/research-chat`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            apikey: SUPABASE_ANON_KEY,
          },
          body: JSON.stringify(localSession ? { query, session_id: localSession, history } : { query, history }),
          signal: ac.signal,
        });

        if (res.status === 401) throw new Error("Please sign in again.");
        if (res.status === 402) throw new Error("The hub is busy right now — try again shortly.");
        if (res.status === 403) throw new Error("You don't have access to the research hub.");
        if (!res.ok || !res.body) throw new Error("Something went wrong answering that. Try again.");

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          let idx: number;
          while ((idx = buf.indexOf("\n\n")) >= 0) {
            const block = buf.slice(0, idx);
            buf = buf.slice(idx + 2);
            let event = "";
            let dataStr = "";
            for (const ln of block.split("\n")) {
              if (ln.startsWith("event:")) event = ln.slice(6).trim();
              else if (ln.startsWith("data:")) dataStr = ln.slice(5).trim();
            }
            if (!event || !dataStr) continue;
            let payload: any;
            try {
              payload = JSON.parse(dataStr);
            } catch {
              continue;
            }
            switch (event) {
              case "status":
                patchAssistant({ status: statusPhrase(payload.phase) });
                break;
              case "tool_use":
                patchAssistant({ status: `${toolPhrase(payload.name)}…` });
                break;
              case "tool_result":
                // keep it quiet; clear the searching pill once results land
                patchAssistant({ status: "thinking" });
                break;
              case "text_delta":
                streamedRaw += payload.text || "";
                // Show a marker-stripped copy mid-stream; keep `streamedRaw` raw for the final
                // reconcile + persistence.
                patchAssistant({ content: stripMarkers(streamedRaw), status: undefined });
                break;
              case "citation":
                if (payload.source_id && !cites.has(String(payload.source_id).toLowerCase())) {
                  cites.set(String(payload.source_id).toLowerCase(), {
                    title: payload.title || "Source",
                    axis: payload.axis,
                    chunkId: payload.chunk_id,
                  });
                }
                break;
              case "final_answer": {
                // Fallback only — the live backend never sends this. Reconcile via finalize().
                finalText = payload.text || streamedRaw;
                result = finalize() ?? result;
                break;
              }
              case "done":
                // The backend's last event: no final_answer ever arrives, so reconcile the streamed
                // raw text here into footnote chips + sources.
                result = finalize() ?? result;
                break;
              case "error":
                errored = true;
                settled = true;
                patchAssistant({ error: "Something went wrong answering that. Try again.", done: true, status: undefined });
                break;
            }
          }
        }

        // graceful finish if the server closed the stream without a `done` event
        result = finalize() ?? result;
      } catch (e: any) {
        const aborted = e?.name === "AbortError";
        if (aborted) {
          // Reconcile whatever streamed before the stop so the partial answer keeps its citations.
          result = finalize({ stopped: true }) ?? result;
        } else {
          errored = true;
          settled = true;
          patchAssistant({
            error: e?.message || "Something went wrong answering that. Try again.",
            done: true,
            status: undefined,
          });
        }
      } finally {
        abortRef.current = null;
        setStreaming(false);
      }

      // Persist client-side (the reader backend writes nothing). Skip on error, on an empty turn, or
      // if the user has since switched conversations (stale generation).
      if (!errored && result && (result.raw.trim() || result.sources.length) && genRef.current === myGen) {
        try {
          await persistTurn(query, result.raw, result.sources, localSession);
        } catch {
          // Persistence is best-effort; the live answer already rendered. Stay silent on failure.
        }
      }

      // Turn fully settled (stream + persist) — release the gate so the next send can run.
      busyRef.current = false;
      setBusy(false);

      async function persistTurn(userText: string, rawAnswer: string, sources: SourceRef[], existingSessionId?: string) {
        const { data: userData } = await supabase.auth.getUser();
        const userId = userData.user?.id;
        if (!userId) return;
        // Bail before touching any shared state if the conversation changed underneath us.
        if (genRef.current !== myGen) return;

        let sid = existingSessionId;
        if (!sid) {
          const { data: created, error } = await supabase
            .from("research_chat_sessions")
            .insert({ user_id: userId, title: userText.slice(0, 60), message_count: 0 })
            .select("id")
            .single();
          if (error || !created || genRef.current !== myGen) return;
          sid = created.id;
          countRef.current = 0;
          sessionIdRef.current = sid; // carry the new id forward synchronously for the next turn
          setSessionId(sid);
        }

        // Give the two rows of a turn distinct, ordered timestamps so a reload (ordered by
        // created_at) never sorts the assistant bubble before its user prompt.
        const userTs = new Date();
        const asstTs = new Date(userTs.getTime() + 1);
        const { error: msgErr } = await supabase.from("research_chat_messages").insert([
          { session_id: sid, role: "user", content: userText, created_at: userTs.toISOString() },
          {
            session_id: sid,
            role: "assistant",
            content: rawAnswer,
            citations: sourcesToCitations(sources) as any,
            created_at: asstTs.toISOString(),
          },
        ]);
        if (msgErr) return;

        if (genRef.current === myGen) countRef.current += 2;
        const nowIso = new Date().toISOString();
        await supabase
          .from("research_chat_sessions")
          .update({ last_message_at: nowIso, updated_at: nowIso, message_count: countRef.current })
          .eq("id", sid);

        qc.invalidateQueries({ queryKey: ["research_chat_sessions"] });
      }
    },
    [messages, qc],
  );

  return { messages, streaming, busy, sessionId, send, stop, reset, setMessages };
}
